<template>
  <div class="container-fluid p-0">
    <div class="row g-3">
      <!-- File Upload Card -->
      <div class="col-lg-6 col-md-6">
        <AiChatCard
          :companyId="props.companyId"
          :endpoint="`/api/company/${props.companyId}/portfolio/send-chat`"
          :greeting="$t('ai_chat.greetings.send')"
          @analysis-complete="handleFileAnalysisComplete"
        />
      </div>

      <!-- Order summary -->
      <div class="col-lg-6 col-md-6">
        <div class="card h-100">
          <div class="card-header bg-light py-2">
            <h6 class="card-title mb-0 fw-semibold">
              {{ t("dashboard.portfolio.sendGoOrderSummary") }}
            </h6>
          </div>
          <div class="card-body">
            <form
              @submit.prevent="openReviewModal"
              class="d-flex flex-column h-100"
            >
              <div>
                <div class="mb-3">
                  <label for="recipientCompanyName" class="form-label small">{{
                    t("dashboard.portfolio.recipientCompanyName")
                  }}</label>
                  <SendRecipientPicker
                    v-model="sendingOrder.recipientCompanyName"
                    :displayValue="aiCompanyName"
                    @select="handleRecipientSelected"
                  />
                </div>
                <template v-if="!isHostedRecipient">
                  <div class="mb-3">
                    <label for="recipientRegId" class="form-label small">{{
                      t("dashboard.portfolio.recipient_eecs_account_number")
                    }}</label>
                    <div class="input-group">
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        :class="{ 'is-invalid': showAccountValidationError }"
                        id="recipientRegId"
                        v-model="sendingOrder.recipientEecsAccountNumber"
                        :disabled="isConfirmingOrder"
                      />
                      <span
                        v-if="derivedCountryDisplay"
                        class="input-group-text bg-light text-muted small"
                      >
                        {{ derivedCountryDisplay }}
                      </span>
                    </div>
                    <div
                      v-if="showAccountValidationError"
                      class="form-text text-danger"
                    >
                      {{ t(accountValidationMessageKey) }}
                    </div>
                    <EecsNameMismatchWarning
                      :companyName="sendingOrder.recipientCompanyName"
                      :accountNumber="sendingOrder.recipientEecsAccountNumber"
                    />
                  </div>
                  <div v-if="!isTgeSelection" class="mb-3">
                    <label
                      for="recipientInternalAccount"
                      class="form-label small"
                      >{{
                        t(
                          "dashboard.portfolio.recipient_internal_account_number"
                        )
                      }}</label
                    >
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      id="recipientInternalAccount"
                      v-model="
                        sendingOrder.recipientRegistryInternalAccountNumber
                      "
                      :disabled="isConfirmingOrder"
                      :placeholder="
                        t(
                          'dashboard.portfolio.recipient_internal_account_placeholder'
                        )
                      "
                    />
                    <div class="form-text text-muted">
                      {{
                        t("dashboard.portfolio.recipient_internal_account_help")
                      }}
                    </div>
                  </div>
                  <div v-if="requiresUnitPrice" class="mb-3">
                    <label for="unitPricePln" class="form-label small">{{
                      t("go_send.unit_price_pln_label")
                    }}</label>
                    <input
                      type="text"
                      inputmode="decimal"
                      class="form-control form-control-sm"
                      :class="{ 'is-invalid': showUnitPriceValidationError }"
                      id="unitPricePln"
                      v-model="sendingOrder.unitPricePln"
                      :disabled="isConfirmingOrder"
                    />
                    <div
                      v-if="showUnitPriceValidationError"
                      class="form-text text-danger"
                    >
                      {{ t("validations.unit_price_pln_format") }}
                    </div>
                    <div class="form-text text-muted">
                      {{ t("go_send.unit_price_pln_help") }}
                    </div>
                  </div>
                </template>
                <div
                  v-else
                  class="alert alert-subtle-secondary d-flex align-items-start gap-2 mb-3"
                >
                  <FontAwesomeIcon
                    icon="fas fa-circle-info"
                    class="mt-1 flex-shrink-0 text-muted"
                  />
                  <span class="small opacity-75">
                    {{ t("go_send.hosted_delivery_info") }}
                  </span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasMixedAuthoritySelection" class="row g-3 mt-3">
      <div class="col-12">
        <div class="alert alert-danger">
          <FontAwesomeIcon icon="exclamation-triangle" class="me-2" />
          {{ t("go_send.mixed_registry_selection_error") }}
        </div>
      </div>
    </div>

    <!-- Insufficient Inventory Warning -->
    <div v-if="showInsufficientInventoryWarning" class="row g-3 mt-3">
      <div class="col-12">
        <div class="alert alert-warning">
          <FontAwesomeIcon icon="exclamation-triangle" class="me-2" />
          {{
            t("go_send.insufficient_inventory_warning", {
              availableMwh: totalAvailableMwhInInventory,
              neededMwh: totalMwhRequestedFromFile ?? totalSelectedMwh,
            })
          }}
        </div>
      </div>
    </div>

    <!-- Add the PortfolioItemsTable below the chat/summary row -->
    <div class="row g-3 mt-3">
      <div class="col-12">
        <h5 class="mb-3">
          {{ t("dashboard.portfolio.selected_certificates") }}
        </h5>
        <PortfolioItemsTable
          ref="portfolioTableRef"
          :selection="selection"
          :enable-count-editing="true"
          :default-visible-columns="TRANSACTION_COLUMNS"
          storage-context="send"
          @update:filtered-stats="onFilteredStats"
        >
          <template #footer-action>
            <button
              type="button"
              class="btn btn-success"
              @click="openReviewModal"
              :disabled="!isOrderComplete || isConfirmingOrder"
            >
              <span
                v-if="isConfirmingOrder"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {{ t("common.actions.confirm_send") }}
            </button>
          </template>
        </PortfolioItemsTable>
      </div>
    </div>

    <SendGoReviewModal
      ref="reviewModalRef"
      :recipientCompanyName="sendingOrder.recipientCompanyName"
      :recipientCountryDisplay="derivedCountryDisplay"
      :recipientEecsAccountNumber="sendingOrder.recipientEecsAccountNumber"
      :recipientRegistryInternalAccountNumber="
        sendingOrder.recipientRegistryInternalAccountNumber
      "
      :unitPricePln="submittableUnitPricePln"
      :isHostedRecipient="isHostedRecipient"
      :totalMwh="totalSelectedMwh"
      :technologies="selectedTechnologies"
      :productionMonths="selectedProductionMonths"
      :productionSupport="summarySupport"
      :isLoading="isConfirmingOrder"
      :confirmDisabled="!isOrderComplete"
      @confirm="handleConfirmSendOrder"
    />
  </div>
</template>

<script setup lang="ts">
import EecsNameMismatchWarning from "@/components/common/EecsNameMismatchWarning.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import {
  type CertificateSelectionCriteria,
  resolveCertificateSelectionFilters,
} from "@/dashboard/inventory_management/common/cancelAiMapping";
import { isItemAvailable } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import {
  type SelectableFilterColumn,
  TRANSACTION_COLUMNS,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { useSelectedPortfolioSummary } from "@/dashboard/inventory_management/common/useSelectedPortfolioSummary";
import SendGoReviewModal from "@/dashboard/inventory_management/send/SendGoReviewModal.vue";
import SendRecipientPicker, {
  type RecipientSelection,
} from "@/dashboard/inventory_management/send/SendRecipientPicker.vue";
import {
  type FilteredPortfolioStats,
  type FlattenedPortfolioItem,
  PortfolioItemTransfer,
} from "@/types";
import { formatCountryWithFlag } from "@/utils/countries";
import { getCountryFromEecs } from "@/utils/eecs";
import {
  AUTHORITY_ACCOUNT_MESSAGE_KEYS,
  isValidRegistryAccountNumber,
  type RegistrationAuthority,
  registrationAuthorityForRegion,
  registryCountryForAccount,
} from "@/utils/registryAccounts";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import AiChatCard from "../common/AiChatCard.vue";

interface SendingOrder {
  recipientEecsAccountNumber: string;
  recipientCompanyName: string;
  recipientCompanyId: number | null;
  unitPricePln: string;
  operation_notes?: string;
  recipientRegistryInternalAccountNumber?: string;
}

const props = defineProps<{
  companyId: string | number;
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler, globalWarningHandler } =
  useGlobalHandlers();
const portfolioItemsStore = usePortfolioItemsStore();
const selection = usePortfolioSelection({ syncToUrl: true });
const {
  selectedPortfolioItems,
  totalSelectedMwh,
  selectedTechnologies,
  selectedProductionMonths,
  summarySupport,
} = useSelectedPortfolioSummary(selection);

const isConfirmingOrder = ref(false);
const aiCompanyName = ref<string>();
const totalMwhRequestedFromFile = ref<number | null>(null);
const reviewModalRef = ref<{ open: () => void; close: () => void } | null>(
  null
);
const portfolioTableRef = ref<InstanceType<typeof PortfolioItemsTable> | null>(
  null
);

// Items currently visible after table filters, delivered reactively via `@update:filtered-stats`.
const filteredItems = ref<FlattenedPortfolioItem[]>([]);

const onFilteredStats = (stats: FilteredPortfolioStats) => {
  filteredItems.value = stats.items;
};

const UNIT_PRICE_PATTERN = /^\d+([.,]\d{1,2})?$/;

const INITIAL_SENDING_ORDER: SendingOrder = {
  recipientEecsAccountNumber: "",
  recipientCompanyName: "",
  recipientCompanyId: null,
  unitPricePln: "",
  operation_notes: undefined,
  recipientRegistryInternalAccountNumber: "",
};

const sendingOrder = ref<SendingOrder>({ ...INITIAL_SENDING_ORDER });

const totalAvailableMwhInInventory = computed(() => {
  return portfolioItemsStore.availableItems.reduce((total, item) => {
    const mwh = Number(item.count);
    return total + (isNaN(mwh) || mwh < 0 ? 0 : Math.floor(mwh));
  }, 0);
});

const showInsufficientInventoryWarning = computed(() => {
  const mwhToCheck = totalMwhRequestedFromFile.value ?? totalSelectedMwh.value;
  return mwhToCheck > 0 && totalAvailableMwhInInventory.value < mwhToCheck;
});

const isHostedRecipient = computed(
  () => sendingOrder.value.recipientCompanyId != null
);

const selectedAuthorities = computed(
  () =>
    new Set(
      selectedPortfolioItems.value.map((item) =>
        registrationAuthorityForRegion(item.registry_region)
      )
    )
);

// NOTE: a transfer always executes inside one authority's registry — there is no
// inter-authority transfer — so a selection spanning two of them cannot be sent at all.
const hasMixedAuthoritySelection = computed(
  () => selectedAuthorities.value.size > 1
);

const selectionAuthority = computed<RegistrationAuthority>(() =>
  selectedAuthorities.value.size === 1
    ? [...selectedAuthorities.value][0]
    : "AIB"
);

// NOTE: TGE is an island — it only moves certificates PL to PL, so an RGP selection always
// goes to a TGE member code.
const isTgeSelection = computed(() => selectionAuthority.value === "RGP");

// NOTE: hosted sends route to Soldera's own TGE account, where the transfer records a
// placeholder price, so only an external TGE recipient needs a user-entered unit price.
const requiresUnitPrice = computed(
  () => isTgeSelection.value && !isHostedRecipient.value
);

// NOTE: AIB keeps prefix-based derivation so the badge appears while the number is still
// being typed; RGP and REGO account numbers carry no prefix, so only a fully valid value
// resolves (a TGE member code is PL by construction, an Ofgem account GB).
const derivedCountryCode = computed(() =>
  selectionAuthority.value === "AIB"
    ? getCountryFromEecs(sendingOrder.value.recipientEecsAccountNumber)
    : registryCountryForAccount(
        sendingOrder.value.recipientEecsAccountNumber,
        selectionAuthority.value
      )
);

const derivedCountryDisplay = computed(() => {
  const code = derivedCountryCode.value;
  return code ? formatCountryWithFlag(code) : "";
});

const isAccountNumberValid = computed(() => {
  const account = sendingOrder.value.recipientEecsAccountNumber.trim();
  if (!account) return false;
  return isValidRegistryAccountNumber(account, selectionAuthority.value);
});

const showAccountValidationError = computed(
  () =>
    !!sendingOrder.value.recipientEecsAccountNumber &&
    !isAccountNumberValid.value
);

const accountValidationMessageKey = computed(
  () => AUTHORITY_ACCOUNT_MESSAGE_KEYS[selectionAuthority.value]
);

const isUnitPriceValid = computed(() => {
  const raw = sendingOrder.value.unitPricePln.trim();
  return UNIT_PRICE_PATTERN.test(raw) && Number(raw.replace(",", ".")) > 0;
});

const showUnitPriceValidationError = computed(
  () => !!sendingOrder.value.unitPricePln.trim() && !isUnitPriceValid.value
);

const submittableUnitPricePln = computed(() =>
  requiresUnitPrice.value && isUnitPriceValid.value
    ? Number(sendingOrder.value.unitPricePln.trim().replace(",", ".")).toFixed(
        2
      )
    : undefined
);

const hasRecipientAddress = computed(() => {
  if (isHostedRecipient.value) {
    return true;
  }
  return isAccountNumberValid.value;
});

const isOrderComplete = computed(() => {
  return (
    hasRecipientAddress.value &&
    !hasMixedAuthoritySelection.value &&
    (!requiresUnitPrice.value || isUnitPriceValid.value) &&
    !!sendingOrder.value.recipientCompanyName &&
    selectedPortfolioItems.value.length > 0 &&
    totalSelectedMwh.value > 0 &&
    !isConfirmingOrder.value
  );
});

const openReviewModal = () => {
  if (isOrderComplete.value) {
    reviewModalRef.value?.open();
  }
};

const handleConfirmSendOrder = async () => {
  if (!isOrderComplete.value || isConfirmingOrder.value) return;
  isConfirmingOrder.value = true;
  try {
    const orderPayload: {
      recipient_eecs_account_number: string;
      recipient_company_name: string;
      recipient_company_id?: number | null;
      portfolio_items: PortfolioItemTransfer[];
      recipient_registry_internal_account_number?: string;
      unit_price_pln?: string;
    } = {
      recipient_eecs_account_number:
        sendingOrder.value.recipientEecsAccountNumber,
      recipient_company_name: sendingOrder.value.recipientCompanyName,
      recipient_company_id: sendingOrder.value.recipientCompanyId,
      portfolio_items: selection.selectedItemsForApi,
    };
    if (submittableUnitPricePln.value) {
      orderPayload.unit_price_pln = submittableUnitPricePln.value;
    }
    const internalAccount =
      sendingOrder.value.recipientRegistryInternalAccountNumber?.trim();
    if (internalAccount && !isHostedRecipient.value) {
      orderPayload.recipient_registry_internal_account_number = internalAccount;
    }
    const currentCompanyId = Number(props.companyId);
    if (isNaN(currentCompanyId)) {
      globalErrorHandler(t("errors.missingCompanyId"));
      isConfirmingOrder.value = false;
      return;
    }
    await goSolidApi.post(
      `/api/company/${currentCompanyId}/portfolio/send`,
      orderPayload
    );

    // Clear local state
    selection.clearSelection();
    totalMwhRequestedFromFile.value = null;
    sendingOrder.value = { ...INITIAL_SENDING_ORDER };
    aiCompanyName.value = undefined;

    // Refetch portfolio items from server
    await portfolioItemsStore.refetchItems();

    globalSuccessHandler(
      t("common.success"),
      t("dashboard.portfolio.sendGoSuccessMessage")
    );
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    isConfirmingOrder.value = false;
  }
};

const handleRecipientSelected = (selection: RecipientSelection) => {
  if (!selection) {
    return;
  }
  sendingOrder.value.recipientCompanyId = selection.recipient_company_id;
  sendingOrder.value.recipientCompanyName = selection.recipient_company_name;
  sendingOrder.value.recipientEecsAccountNumber =
    selection.recipient_eecs_account_number;
  if (selection.recipient_company_id != null) {
    // Hosted recipients never carry an external EECS / internal account number;
    // clear any stale values left from a prior external entry.
    sendingOrder.value.recipientEecsAccountNumber = "";
    sendingOrder.value.recipientRegistryInternalAccountNumber = "";
  }
  aiCompanyName.value = undefined;
};

const handleFileAnalysisComplete = async (data: any) => {
  if (!data) {
    return;
  }

  try {
    const formData = data.form_data;

    totalMwhRequestedFromFile.value = null;

    if (formData.recipient_company_name) {
      aiCompanyName.value = formData.recipient_company_name;
      sendingOrder.value.recipientCompanyName = formData.recipient_company_name;
    }

    if (formData.recipient_eecs_account_number) {
      sendingOrder.value.recipientEecsAccountNumber =
        formData.recipient_eecs_account_number;
    }

    if (
      formData &&
      Object.prototype.hasOwnProperty.call(
        formData,
        "recipient_registry_internal_account_number"
      )
    ) {
      const internalAccount =
        formData.recipient_registry_internal_account_number;
      sendingOrder.value.recipientRegistryInternalAccountNumber =
        typeof internalAccount === "string" ? internalAccount.trim() : "";
    }

    const criteria = mapSendCriteriaToSelection(formData?.selection_criteria);
    const totalMwh =
      typeof data.total_mwh_requested === "number"
        ? data.total_mwh_requested
        : null;

    if (criteria || totalMwh) {
      // Apply the AI's document criteria as table filters (leaving any manual filters on other
      // columns intact), then select oldest-first from the resulting filtered set — so the picks
      // honor both the AI's criteria AND the operator's active filters, matching the cancel tab.
      const selectedMwh = await applyCriteriaAndSelect(criteria, totalMwh);
      if (selectedMwh <= 0) {
        // The criteria matched no available certificates (unavailable technology/device/period, or a
        // stale manual filter), so the table is empty and nothing was selected. Don't claim success —
        // tell the operator to review the criteria instead of leaving a confusing "all set" toast.
        globalWarningHandler(
          t("dashboard.portfolio.fileUpload.analysisNoMatchTitle"),
          t("dashboard.portfolio.fileUpload.analysisNoMatchMessage")
        );
      } else if (totalMwh && selectedMwh < totalMwh) {
        // Some stock matched, but the filtered inventory can't cover the requested volume — the
        // transfer would be under-filled. Warn instead of reporting success so the operator reviews
        // the shortfall before confirming (the persistent insufficient-inventory banner also shows).
        globalWarningHandler(
          t("dashboard.portfolio.fileUpload.analysisShortfallTitle"),
          t("dashboard.portfolio.fileUpload.analysisShortfallMessage", {
            selected: selectedMwh,
            requested: totalMwh,
          })
        );
      } else {
        globalSuccessHandler(
          t("dashboard.portfolio.fileUpload.analysisSuccessTitle"),
          t("dashboard.portfolio.fileUpload.analysisSuccessMessage")
        );
      }
    } else {
      // A completed analysis with no selection criteria and no requested volume must still clear any
      // selection and AI-controlled filters left over from a previous document — otherwise
      // usePortfolioSelection carries the prior picks into selectedItemsForApi and could submit
      // certificates from an earlier upload.
      resetAiControlledFilters();
      selection.clearSelection();
    }

    if (totalMwh) {
      totalMwhRequestedFromFile.value = totalMwh;
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

// The table columns that resolveCertificateSelectionFilters can drive from document criteria. Reset
// these between uploads so one document's criteria never linger as hidden filters on the next.
const AI_CONTROLLED_FILTER_COLUMNS: SelectableFilterColumn[] = [
  "technology_type",
  "production_month",
  "issuing_region",
  "production_device_name",
  "portfolio_name",
];

// Clears every AI-controllable column filter, so a previous upload's criteria never linger. Manual
// filters on other columns are left intact. No-op if the table ref isn't mounted yet.
const resetAiControlledFilters = () => {
  portfolioTableRef.value?.clearFilters(AI_CONTROLLED_FILTER_COLUMNS);
};

// Send's extraction criteria (ISO YYYY-MM-DD periods) reshaped into the shared resolver's
// CertificateSelectionCriteria (YYYY-MM vintage months). Only the columns send extracts are set.
//
// Fail-closed on unparseable production bounds: when a period bound is PRESENT in the raw payload
// but does NOT parse to YYYY-MM, return a criteria object with the unparseable bound so the shared
// resolver `resolveCertificateSelectionFilters` applies its unmatchable-sentinel path (which yields
// an empty table and fires the selectedCount===0 warning, matching the cancel tab's behavior).
const mapSendCriteriaToSelection = (
  raw: any
): CertificateSelectionCriteria | null => {
  if (!raw) {
    return null;
  }
  // Returns the parsed YYYY-MM vintage month if parseable, or the SENTINEL for present-but-unparseable.
  const UNPARSEABLE_SENTINEL = "UNPARSEABLE";
  const toVintageMonth = (value: unknown, fieldName: string): string | null => {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const match = /^(\d{4})-(\d{2})/.exec(trimmed);
    if (match) {
      return `${match[1]}-${match[2]}`;
    }
    // Present but unparseable: return a sentinel that resolveCertificateSelectionFilters will fail-close on.
    console.warn(
      `[SendGoComponent] ${fieldName} is present but unparseable: "${trimmed}". Failing closed.`
    );
    return UNPARSEABLE_SENTINEL;
  };
  const cleanList = (value: unknown): string[] | null => {
    if (!Array.isArray(value)) return null;
    const cleaned = value
      .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
      .filter((entry): entry is string => !!entry);
    return cleaned.length ? cleaned : null;
  };
  const criteria: CertificateSelectionCriteria = {
    technologyTypes: cleanList(raw.technology_types),
    productionPeriodStart: toVintageMonth(
      raw.production_period_start,
      "production_period_start"
    ),
    productionPeriodEnd: toVintageMonth(
      raw.production_period_end,
      "production_period_end"
    ),
    issuingRegions: null,
    registryRegions: null,
    codAge: null,
    productionSupport: null,
    ecolabels: null,
    productionDeviceNames: cleanList(raw.production_device_names),
    portfolioNames: null,
  };
  return Object.values(criteria).some((value) => value !== null)
    ? criteria
    : null;
};

// Selects oldest-vintage-first from the reactively filtered set, so document criteria (applied as
// table filters) and any manual operator filters are both honored. Mirrors the cancel tab.
// Returns the total MWh selected, so callers can tell whether the criteria matched anything.
const selectFromFilteredForMwh = (mwhNeeded: number | null): number => {
  selection.clearSelection();

  const availableItems = filteredItems.value.filter(isItemAvailable);
  const sortedItems = [...availableItems].sort((a, b) => {
    const monthA = a.production_month ?? "9999-99";
    const monthB = b.production_month ?? "9999-99";
    if (monthA !== monthB) {
      return monthA.localeCompare(monthB);
    }
    return a.count - b.count;
  });

  // No MWh target: select the whole filtered set (mirrors the backend's filters-only strategy).
  if (mwhNeeded === null || mwhNeeded <= 0) {
    let selected = 0;
    sortedItems.forEach((item) => {
      selection.updateItemSelectedCount(item.id, item.count);
      selected += item.count;
    });
    return selected;
  }

  let remaining = mwhNeeded;
  for (const item of sortedItems) {
    if (remaining <= 0) break;
    const selectCount = Math.min(item.count, remaining);
    selection.updateItemSelectedCount(item.id, selectCount);
    remaining -= selectCount;
  }
  return mwhNeeded - Math.max(remaining, 0);
};

// Returns the total MWh actually selected (0 when the criteria matched no available certificates).
const applyCriteriaAndSelect = async (
  criteria: CertificateSelectionCriteria | null,
  totalMwh: number | null
): Promise<number> => {
  if (criteria && portfolioTableRef.value) {
    const resolved = resolveCertificateSelectionFilters(
      criteria,
      portfolioTableRef.value.getFilterOptions()
    );
    // Reset every AI-controllable column first (a device filter from an earlier document would
    // otherwise silently empty this one's table), then apply this document's resolved set — leaving
    // the operator's manual filters on other columns intact.
    portfolioTableRef.value.clearFilters(AI_CONTROLLED_FILTER_COLUMNS);
    portfolioTableRef.value.applyColumnFilters(resolved.filters);
    // Let the table re-run filtering so `filteredItems` reflects the new criteria before selecting.
    // The table emits update:filtered-stats from a post-render effect, so one flush is enough.
    await nextTick();
  }
  return selectFromFilteredForMwh(totalMwh);
};

watch(
  () => sendingOrder.value.recipientCompanyName,
  (newValue, oldValue) => {
    if (
      aiCompanyName.value &&
      newValue !== aiCompanyName.value &&
      newValue !== oldValue
    ) {
      aiCompanyName.value = undefined;
    }
  }
);

watch(requiresUnitPrice, (required) => {
  if (!required) {
    sendingOrder.value.unitPricePln = "";
  }
});

onUnmounted(() => {
  selection.clearSelection();
});
</script>

<style scoped lang="scss"></style>
